import { IFilterService, IWindowService } from 'angular';
import { IStateService } from 'angular-ui-router';
import * as _ from 'lodash';
import * as moment from 'moment';
import { Observable, Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { ISubscription } from 'rxjs/Subscription';
import { ISdcStore, IStoreScope } from 'src/app/sdc/flux/sdc-store.interface';
import { StoreListenerUtils } from 'src/app/shared/services/store-listener-utils/store-listener-utils.service';
import { ImUser } from 'Src/ng2/shared/services/im-models/im-user';
import { UtilitiesService } from 'src/ng2/shared/services/utilities/utilities.service';
import { StudentSupportStatuses } from '../../../../ng2/shared/constants/student-support-statuses.constant';
import { SupportStatuses } from '../../../../ng2/shared/constants/support-statuses.constant';
import { IStudentSupport } from '../../../../ng2/shared/typings/interfaces/student-support.interface';
import { ISchoolSupport } from '../../../../ng2/shared/typings/interfaces/support.interface';
import { ImStudentSupport } from '../../../shared/static-models/im-models/im-student-support.service';
import { ImSupport } from '../../../shared/static-models/im-models/im-support.service';
import { SupportsRosterExporterService } from './../../../features/supports/supports-roster/supports-roster-exporter';
import { SdcActionsService } from './../../../sdc/flux/sdc-actions.service';
import { SpinnerModal } from './../../../shared/modals/status-spinner/status-spinner.service';
import { ImAttendanceRecord } from './../../../shared/static-models/im-models/im-attendance-record.service';

interface ISidebar {
  name: string;
  _schoolYear: string;
  _category: string;
  _activityLead: string;
  _dates: string;
  _meetsOn: string;
  _meetsAt: string;
  location: string;
}
/* istanbul ignore next */
export class SupportsRosterCtrl {
  static $inject = [
    '$scope',
    '$state',
    '$window',
    '$filter',
    '$mdDialog',
    'Support',
    'StudentSupport',
    'SupportAttendanceService',
    'ConfirmationModal',
    'UtilitiesService',
    'SupportsRosterExporterService',
    'CsvExporter',
    'ErrorModal',
    'sdcActions',
    'BackgroundJobSpinner',
    'SpinnerModal',
    'Rollbar',
    'ImSupport',
    'ImStudentSupport',
    'SDCStore',
    'StoreListenerUtils',
    'ImAttendanceRecord',
    'ImUser',
  ];

  constructor(
    public $scope: IStoreScope,
    private $state: IStateService,
    private $window: IWindowService,
    private $filter: IFilterService,
    private $mdDialog: any,
    private Support: any,
    private StudentSupport: any,
    private SupportAttendanceService,
    private ConfirmationModal,
    private UtilitiesService: UtilitiesService,
    private SupportsRosterExporterService: SupportsRosterExporterService,
    private CsvExporter,
    private ErrorModal,
    private sdcActions: SdcActionsService,
    private BackgroundJobSpinner,
    private SpinnerModal: SpinnerModal,
    private Rollbar: any,
    private imSupport: ImSupport,
    private imStudentSupport: ImStudentSupport,
    private SDCStore: ISdcStore,
    private storeListenerUtils: StoreListenerUtils,
    private imAttendanceRecord: ImAttendanceRecord,
    private imUser: ImUser,
  ) {}

  // BINDINGS
  schoolSupports: ISchoolSupport[]; // All supports for this school
  studentSupports$: Observable<IStudentSupport[]>;
  attendanceRecords; // All attendance records for this school
  // FILTERED BINDINGS
  activeSchoolSupportsSortedByName: ISchoolSupport[]; // Only supports with active backend status
  studentSupportsHashedBySupportId; // All student supports hashed by support Id
  attendanceRecordsHashedBySupportId; // All attendance records hashed by support Id
  // HEADER & SIDEBAR
  sidebar;
  currentSupport;
  countInProgressStudentSupports;
  countNotStartedStudentSupports;
  countCompletedStudentSupports;
  countNotRemovedStudentSupports;

  // TABLE
  table;
  reverseSort;
  sortField;
  selectedRows: any[];

  // School Supports FrontEnt Status
  private supportsGroupedByFrontEndStatus: { [key: string]: ISchoolSupport[] };
  private selectedSupportsFrontEndStatus: string;
  public supportsGroupedByFrontEndStatusKeys: string[];
  public supportsFrontEndStatusHumanReadable: { [key: string]: string };

  // helpers
  private initialSortFields;
  private removedAndCompletedStudentSupports;

  studentSupportsSubscription: ISubscription;
  studentSupports: IStudentSupport[];

  public destroy$: Subject<boolean> = new Subject<boolean>();

  $onInit() {
    this.storeListenerUtils.startListeningAndLoad(this, [['schoolSupports'], ['studentSupports'], ['students']]);
  }

  public storedSchoolSupports;

  public setUi(): void {
    this.studentSupportsSubscription = this.studentSupports$
      .pipe(
        tap((studentSupports: IStudentSupport[]) => {
          this.studentSupports = studentSupports;
          this.setView();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  setView(): void {
    const storedSchoolSupports = angular.copy(this.SDCStore.getAllSchoolSupports());
    const schoolSupports: ISchoolSupport[] = this.Support.getNotDeletedSupports(storedSchoolSupports);

    // HASH STUDENT SUPPORTS BY SUPPORT ID TO MAKE LOOKING UP EASIER
    this.studentSupportsHashedBySupportId = this.StudentSupport.hashStudentSupportsBySupportId(this.studentSupports);

    // HASH ATTENDANCE RECORDS BY SUPPORT ID TO MAKE LOOKING UP EASIER
    this.attendanceRecordsHashedBySupportId = this.imAttendanceRecord.hashAttendanceRecordsBySupportId(
      this.attendanceRecords,
    );

    // SET INITIAL SORT
    this.initialSortFields = ['humanReadableStatus'];
    this.sortField = 'humanReadableStatus';
    this.reverseSort = false;

    // SET INITIAL FILTERED SCHOOL_SUPPORTS BY FRONTEND STATUS
    this.supportsGroupedByFrontEndStatus = this.imSupport.getSupportsGroupedByFrontEndStatus(schoolSupports);
    this.supportsGroupedByFrontEndStatusKeys = Object.keys(this.supportsGroupedByFrontEndStatus);
    this.supportsFrontEndStatusHumanReadable = SupportStatuses.frontendHumanReadable;
    this.selectedSupportsFrontEndStatus = this.supportsGroupedByFrontEndStatusKeys[0];

    // FIND ALL ACTIVE SUPPORTS TO POPULATE IN SELECT MENU
    this.activeSchoolSupportsSortedByName = this.supportsGroupedByFrontEndStatus[this.selectedSupportsFrontEndStatus];
    const selectedSupport = this.activeSchoolSupportsSortedByName[0];

    this.selectSupport(selectedSupport);
    this.SpinnerModal.deactivateSpinner();
  }

  // PUBLIC -
  getSortIcon(field) {
    return _.includes(this.initialSortFields, field);
  }

  activeSort(field) {
    return field === this.sortField || _.includes(this.initialSortFields, field);
  }

  tableRowIsSelected(tableRow) {
    return this.selectedRows.indexOf(tableRow) > -1;
  }

  isIndeterminate() {
    return this.selectedRows.length !== 0 && this.selectedRows.length !== this.table.length;
  }

  isChecked() {
    return this.selectedRows.length === this.table.length;
  }

  private renderSidebar(currentSupport: ISchoolSupport): ISidebar {
    const { name, location } = currentSupport;

    const sidebar = {
      name,
      _schoolYear: this.imSupport.getFormattedSchoolYear(currentSupport),
      _category: this.imSupport.getCategoryHumanName(currentSupport),
      _activityLead: this.imSupport.getLeadHumanName(currentSupport),
      _dates: this.imSupport.getStartAndEndDateString(currentSupport),
      _meetsOn: this.imSupport.getMeetsOnScheduleString(currentSupport),
      _meetsAt: this.imSupport.getStartAndEndTimeString(currentSupport),
      location,
    };

    return sidebar;
  }

  private renderTableRow(studentSupport: IStudentSupport, studentAttendanceSummary) {
    const { _id, status, student, assignedBy, markedCompleteBy } = studentSupport;
    const { lastFirst, studentId } = student;
    let attendanceObj;
    if (studentAttendanceSummary) {
      const { percentPres, percentAbs, percentLate, daysAbsent, total } = studentAttendanceSummary;
      attendanceObj = {
        percentPres: percentPres + '%',
        percentAbs: percentAbs + '%',
        percentLate: percentLate + '%',
        daysAbsent,
        logs: total,
      };
    } else {
      attendanceObj = null;
    }

    // TODO instance methods to be replaced with calls to ImStudentSupport once merged - JYR
    const tableRow = {
      studentSupport, // needed for marking supports complete or removed - DS
      studentSupportId: _id, // needed for marking supports complete or removed - DS
      studentId,
      lastFirst,
      markedCompleteBy,
      assignedBy,
      attendanceObj,
      studentIdTrimmed: this.imStudentSupport.getStudentIdFrom_Id(studentSupport),
      assignedOnString: this.imStudentSupport.getAssignedOnString(studentSupport),
      startsOnString: this.imStudentSupport.getStartsOnString(studentSupport),
      reviewOnString: this.imStudentSupport.getReviewOnString(studentSupport),
      completedAtString: this.imStudentSupport.getCompletedAtString(studentSupport) || '-',
      humanReadableStatus: this.imStudentSupport.getFrontendStatus(studentSupport, status),
      assignedByEffectiveEmail: assignedBy ? this.imUser.getEffectiveEmail(assignedBy) : null,
      markedCompleteByEffectiveEmail: markedCompleteBy ? this.imUser.getEffectiveEmail(markedCompleteBy) : null,
    };

    return tableRow;
  }

  private renderTable(
    currentSupport = null,
    studentSupportsHashedBySupportId = null,
    attendanceRecordsHashedBySupportId = null,
  ) {
    let tableRows = [];

    const currentSupportId = currentSupport._id;
    const studentSupportsForCurrentSupport = studentSupportsHashedBySupportId[currentSupportId];
    if (!studentSupportsForCurrentSupport || studentSupportsForCurrentSupport.length === 0) return tableRows;

    const attendanceRecordsForCurrentSupport = attendanceRecordsHashedBySupportId[currentSupportId] || [];
    const attendanceSummariesForCurrentSupport = this.SupportAttendanceService.summarizeAttendanceData(
      attendanceRecordsForCurrentSupport,
      'studentId',
    );
    const attendanceSummariesForCurrentSupportByStudentId = _.reduce(
      attendanceSummariesForCurrentSupport,
      (result, attendanceSummary: any) => {
        const studentId = attendanceSummary.studentId;
        result[studentId] = attendanceSummary;
        return result;
      },
      {},
    );

    tableRows = _.map(studentSupportsForCurrentSupport, (studentSupport: IStudentSupport) => {
      const studentId = studentSupport.student.studentId;
      const studentAttendanceSummary = attendanceSummariesForCurrentSupportByStudentId[studentId];
      const tableRow = this.renderTableRow(studentSupport, studentAttendanceSummary);
      return tableRow;
    });

    return tableRows;
  }

  private getTable(tableData: any[]) {
    const renderedTable = this.renderTable(...tableData);
    const table = this.$filter('orderBy')(renderedTable, this.initialSortFields, this.reverseSort);
    return table;
  }

  // private helpers triggered by public actions
  private setTableCheckboxesAndCounts(tableRows) {
    this.selectedRows = [];
    this.removedAndCompletedStudentSupports = [];
    this.countInProgressStudentSupports = 0;
    this.countNotStartedStudentSupports = 0;
    this.countCompletedStudentSupports = 0;
    this.countNotRemovedStudentSupports = 0;
    _.forEach(tableRows, (tableRow: any) => {
      if (tableRow.humanReadableStatus === StudentSupportStatuses.frontend.IN_PROGRESS) {
        this.countInProgressStudentSupports++;
        this.countNotRemovedStudentSupports++;
      } else if (tableRow.humanReadableStatus === StudentSupportStatuses.frontend.NOT_STARTED) {
        this.countNotStartedStudentSupports++;
        this.countNotRemovedStudentSupports++;
      } else if (tableRow.humanReadableStatus === StudentSupportStatuses.frontend.COMPLETED) {
        this.removedAndCompletedStudentSupports.push(tableRow);
        this.countCompletedStudentSupports++;
        this.countNotRemovedStudentSupports++;
      } else if (tableRow.humanReadableStatus === StudentSupportStatuses.frontend.REMOVED) {
        this.removedAndCompletedStudentSupports.push(tableRow);
      }
    });
  }
  // called by bulkUpdate
  private updateTable(tableRows, selectedStudentSupportIds, newBackendStatus) {
    return _.forEach(tableRows, (tableRow: any) => {
      const { studentSupportId, studentSupport } = tableRow;
      const tableRowShouldBeUpdated = _.includes(selectedStudentSupportIds, studentSupportId);
      if (tableRowShouldBeUpdated) {
        tableRow.humanReadableStatus = this.imStudentSupport.getFrontendStatus(studentSupport, newBackendStatus);
      }
    });
  }

  // no private/public keyword indicates accessed everywhere
  tableRowIsRemovedOrCompleted(tableRow) {
    return _.includes(this.removedAndCompletedStudentSupports, tableRow);
  }

  // public - user triggered
  selectSupport(selectedSupport) {
    this.currentSupport = selectedSupport;
    this.sortField = 'humanReadableStatus';
    this.reverseSort = false;
    this.sidebar = this.renderSidebar(this.currentSupport);

    const tableData = [
      this.currentSupport,
      this.studentSupportsHashedBySupportId,
      this.attendanceRecordsHashedBySupportId,
    ];

    this.table = this.getTable(tableData);
    this.setTableCheckboxesAndCounts(this.table);
  }

  bulkUpdateStudentSupports(newBackendStatus) {
    const cachedTableRows = this.UtilitiesService.copyPOJO(this.table);
    const selectedStudentSupports = _.map(this.selectedRows, (row: any) => {
      return row.studentSupport;
    });
    const selectedStudentSupportIds = _.map(this.selectedRows, 'studentSupportId');
    this.updateTable(this.table, selectedStudentSupportIds, newBackendStatus);
    this.setTableCheckboxesAndCounts(this.table);
    return this.sdcActions
      .updateStudentSupportsAsync(selectedStudentSupportIds, newBackendStatus, selectedStudentSupports)
      .catch(errorResponse => {
        this.ErrorModal.show('Unable to save your changes', errorResponse);
        this.table = cachedTableRows;
        this.setTableCheckboxesAndCounts(this.table);
      });
  }

  sortBy(field) {
    if (_.includes(this.initialSortFields, field) && this.sortField === undefined) {
      this.reverseSort = true;
    } else if (field !== null && this.sortField === field) {
      this.reverseSort = !this.reverseSort;
    } else {
      this.reverseSort = false;
    }
    this.initialSortFields = [];
    this.sortField = field;
    this.table = this.$filter('orderBy')(this.table, this.sortField, this.reverseSort);
  }

  openStudentInSsv(studentId: string): any {
    const url = this.$state.href('main.school.single-student', { studentId });
    this.$window.open(url, '_blank');
  }

  toggle(tableRow) {
    const isRemovedOrCompleted = this.tableRowIsRemovedOrCompleted(tableRow);
    if (isRemovedOrCompleted) {
      return;
    }
    const idx = this.selectedRows.indexOf(tableRow);
    if (idx > -1) {
      this.selectedRows.splice(idx, 1);
    } else {
      // row does not exist. add
      this.selectedRows.push(tableRow);
    }
  }

  toggleAll() {
    const diff = _.difference(this.table, this.removedAndCompletedStudentSupports);
    if (this.selectedRows.length === diff.length) {
      this.selectedRows = [];
    } else if (this.selectedRows.length >= 0) {
      this.selectedRows = diff;
    }
  }

  bulkRemoveStudentsFromSupport() {
    const studentString = this.selectedRows.length > 1 ? 'students' : 'student';
    const studentSupportStatus = StudentSupportStatuses.backend.DELETED;
    const title = 'Confirm';
    const text = `Are you sure you want to remove ${this.selectedRows.length} ${studentString} from this support?`;
    const spinnerMessage = 'Removing students from support...';
    const promise = this.ConfirmationModal.show(title, text, 'REMOVE');

    promise
      .then(() => {
        const backgroundJob = this.bulkUpdateStudentSupports(studentSupportStatus);
        this.BackgroundJobSpinner.activateSpinner(spinnerMessage, backgroundJob);
      })
      .catch(err => {
        this.$mdDialog.hide();
        // err is undefined if user cancels modal (JE)
        if (err) this.Rollbar.error(`Unable to bulk update student supports`, err);
      });
  }

  bulkComplete() {
    const studentString = this.selectedRows.length > 1 ? 'students' : 'student';
    const studentSupportStatus = StudentSupportStatuses.backend.COMPLETED;
    const title = 'Confirm';
    const text = `Are you sure you want to mark ${
      this.selectedRows.length
    } ${studentString} as having completed this support?`;
    const spinnerMessage = 'Marking student supports as complete...';
    const promise = this.ConfirmationModal.show(title, text, 'MARK COMPLETE');

    promise
      .then(() => {
        const backgroundJob = this.bulkUpdateStudentSupports(studentSupportStatus);
        this.BackgroundJobSpinner.activateSpinner(spinnerMessage, backgroundJob);
      })
      .catch(err => {
        this.$mdDialog.hide();
        // err is undefined if user cancels modal (JE)
        if (err) this.Rollbar.error(`Unable to bulk update student supports`, err);
      });
  }

  generateCsv() {
    const docType = 'text/csv;charset=utf-8';
    const currentDateAndTime = moment().format('MM-DD-YYYY hmma');
    const fileName = `SupportsRosterExport-${currentDateAndTime}.csv`;
    const csvString = this.SupportsRosterExporterService.createSupportsRosterCsv(this.table);
    this.CsvExporter.exportCsv(csvString, fileName, docType);
  }

  public selectSupportStatus(selectedSupportsFrontEndStatus: string) {
    this.activeSchoolSupportsSortedByName = this.supportsGroupedByFrontEndStatus[selectedSupportsFrontEndStatus];
    const selectedSupport = this.activeSchoolSupportsSortedByName[0];
    this.selectSupport(selectedSupport);
  }
}
